<!-- eslint-disable vue/attribute-hyphenation -->
<template>
  <b-card
    no-body
  >
    <validation-observer ref="licenceCreate">
      <b-form>
        <b-row>
          <b-col
            md="12"
            class="pl-4 pr-4"
          >
            <b-overlay
              :show="isLoading"
              rounded
              opacity="0.5"
              spinner-small
              spinner-variant="primary"
            >
              <div class="section-block mb-4">
                <div class="site-hardware-actions site-hardware-actions_management wan-settings-header">
                  <div class="section-block__title mb-0">
                    <feather-icon
                      icon="FileTextIcon"
                      width="24"
                      height="24"
                    />
                    <h2>WAN Network Settings</h2>
                  </div>
                  <b-dropdown
                    toggle-class="more-dropdown-transparent"
                    dropdown
                    right
                  >
                    <template v-slot:button-content>
                      <feather-icon
                        icon="MoreVerticalIcon"
                        size="16"
                        class="text-body align-middle"
                        color="#F0554E"
                      />
                    </template>
                    <b-dropdown-item
                      @click="pullFromSite"
                    >
                      <feather-icon
                        icon="DownloadCloudIcon"
                        size="20"
                        class="mr-50"
                      />
                      <span>Pull from</span>
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
                <b-alert
                  :show="isFromController"
                  variant="info"
                  class="p-1 mb-2"
                >
                  These settings were read from the controller and are not saved in NetPanel yet.
                  Use Pull from to save them without changing the controller.
                </b-alert>
                <b-form-group
                  label="Type"
                  label-class="label-class"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Type"
                    vid="type"
                    rules="required"
                  >
                    <v-select
                      v-model="typeActive"
                      :options="types"
                      placeholder="Select type"
                      label="type"
                      :clearable="false"
                      :searchable="false"
                      :dropdown-should-open="() => true"
                      class="no-icon"
                      @input="checkFields"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <div
                  v-if="typeActive.includes('PPPoE')"
                  class="neighbouring-form-container"
                >
                  <b-form-group
                    label="Username"
                    label-class="label-class"
                    class="neighbouring-form-group"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="username"
                      rules="required|max:255"
                    >
                      <b-form-input
                        v-model="form.username"
                        :state="errors.length > 0 ? false:null"
                        placeholder="Username"
                        autocomplete="off"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                  <b-form-group
                    label="Password"
                    label-class="label-class"
                    class="neighbouring-form-group"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="password"
                      rules="required|max:255"
                    >
                      <b-input-group
                        class="input-group-merge"
                        :class="errors.length > 0 ? 'is-invalid':null"
                      >
                        <b-form-input
                          v-model="form.password"
                          class="form-control-merge"
                          :state="errors.length > 0 ? false:null"
                          placeholder="Password"
                          autocomplete="new-password"
                          :type="passwordFieldType"
                        />
                        <b-input-group-append is-text>
                          <feather-icon
                            class="cursor-pointer"
                            :icon="passwordToggleIcon"
                            @click="togglePasswordVisibility"
                          />
                        </b-input-group-append>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </div>
                <div
                  class="neighbouring-form-container"
                >
                  <b-form-group
                    label="Interface"
                    label-class="label-class"
                    class="neighbouring-form-group"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="interface_ip"
                      rules="max:255"
                    >
                      <b-form-ip
                        :errors="errors"
                        placeholder="Interface"
                        autocomplete="off"
                        :onchange="(value) => form.interface_ip = value"
                        :disabled="typeActive.includes('PPPoE')|| typeActive.includes('DHCP Client')"
                        :model="form.interface_ip"
                        inputs-id="interface_ip"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                  <b-form-group
                    label="Subnet Mask"
                    label-class="label-class"
                    class="neighbouring-form-group"
                    :class="{ 'invisible': typeActive.includes('PPPoE') }"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="subnet_mask"
                      rules="max:255"
                    >
                      <b-form-ip
                        :errors="errors"
                        placeholder="Subnet Mask"
                        autocomplete="off"
                        :onchange="(value) => form.subnet_mask = value"
                        :disabled="!typeActive.includes('Static')"
                        :model="form.subnet_mask"
                        inputs-id="subnet_mask"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                  <b-form-group
                    v-if="!typeActive.includes('PPPoE')"
                    label="Gateways Address"
                    label-class="label-class"
                    class="neighbouring-form-group"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="gateway_ip"
                      rules="max:255"
                    >
                      <b-form-ip
                        :errors="errors"
                        placeholder="Gateways Address"
                        autocomplete="off"
                        :onchange="(value) => form.gateway_ip = value"
                        :disabled="typeActive.includes('DHCP Client')"
                        :model="form.gateway_ip"
                        inputs-id="gateway_ip"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                  <b-form-group
                    v-if="typeActive.includes('PPPoE')"
                    label="Network Address"
                    label-class="label-class"
                    class="neighbouring-form-group"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="network_ip"
                      rules="max:255"
                    >
                      <b-form-ip
                        :errors="errors"
                        placeholder="Network Address"
                        autocomplete="off"
                        :onchange="(value) => form.network_ip = value"
                        :disabled="true"
                        :model="form.network_ip"
                        inputs-id="network_ip"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </div>
                <div
                  v-if="typeActive.includes('PPPoE')"
                  class="neighbouring-form-container"
                >
                  <b-form-group
                    label="Port"
                    label-class="label-class"
                    class="neighbouring-form-group"
                  >
                    <b-form-input
                      :value="pppoePort"
                      placeholder="Port"
                      disabled
                    />
                  </b-form-group>
                  <b-form-group
                    label="Connection"
                    label-class="label-class"
                    class="neighbouring-form-group"
                  >
                    <div class="d-flex align-items-center wan-pppoe-connection">
                      <b-spinner
                        v-if="isPppoeStatusPolling"
                        variant="primary"
                        small
                        class="mr-50"
                      />
                      <span :class="pppoeStatusClass">{{ pppoeStatusText }}</span>
                    </div>
                  </b-form-group>
                </div>
                <div class="d-flex justify-content-between">
                  <div
                    v-if="isSaveButtonDisabled && saveButtonError"
                    class="text-danger d-flex align-items-center mr-3"
                  >
                    Failed to fetch the data for {{ saveButtonError }}. Try again later, or contact support.
                  </div>
                  <div v-else />
                  <div class="double-button">
                    <b-button-request
                      type="reset"
                      text="Reset"
                      :variant="outline-secondary"
                      :request="resetForm"
                    />
                    <b-button-request
                      text="Save"
                      :request="validationForm"
                      :disabled="isSaveButtonDisabled"
                    />
                  </div>
                </div>
              </div>
            </b-overlay>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
    <b-modal
      ref="modalLoader"
      title="Pulling..."
      hide-footer
      centered
      hide-close
      no-close-on-esc
      no-close-on-backdrop
      hide-header-close
      size="sm"
    >
      <div class="d-flex justify-content-center align-items-center mt-2 mb-2">
        <b-spinner
          variant="primary"
          label="Spinning"
          style="width: 3rem; height: 3rem;"
        />
      </div>
    </b-modal>
  </b-card>
</template>

<script>

import {
  BAlert, BCard, BForm, BFormGroup, BFormInput, BInputGroup, BInputGroupAppend, BRow, BCol, BOverlay, BDropdown, BDropdownItem, BModal,
  BSpinner,
} from 'bootstrap-vue'
import { required, max } from '@validations'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import axios from '../../axios-resolver'
import BFormIp from '../../../../components/BFormIP.vue'
import BButtonRequest from '../../../../components/BButtonRequest.vue'

// RouterOS needs a few seconds to dial and authenticate after the settings are applied.
const PPPOE_STATUS_INTERVAL = 5000
const PPPOE_STATUS_ATTEMPTS = 6

export default {
  components: {
    BAlert,
    BButtonRequest,
    BCard,
    BForm,
    BFormGroup,
    BRow,
    BOverlay,
    BCol,
    BDropdown,
    BDropdownItem,
    BModal,
    BSpinner,
    vSelect,
    BFormIp,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  props: {
    hardwareId: {
      required: true,
      type: Number,
    },
    siteId: {
      required: true,
      type: Number,
    },
  },
  data() {
    return {
      isLoading: true,
      isFromController: false,
      pppoeStatus: null,
      isPppoeStatusPolling: false,
      pppoeStatusTimer: null,
      form: {
        type: '',
        interface_ip: '',
        subnet_mask: '',
        gateway_ip: '',
        username: '',
        password: '',
        network_ip: '',
        dhcp: {
          interface_ip: '',
          subnet_mask: '',
          gateway_ip: '',
        },
        pppoe: {
          interface_ip: '',
          network_ip: '',
        },
        static: {
          interface_ip: '',
          subnet_mask: '',
          gateway_ip: '',
        },
      },
      validToSave: {
        dhcp: true,
        static: true,
        pppoe: true,
      },
      typeActive: '',
      typesStatic: ['DHCP Client', 'Static', 'PPPoE'],
      types: ['DHCP Client', 'Static', 'PPPoE'],
      itemToEdit: null,
      required,
      max,
    }
  },
  // computed: {
  //   isSaveButtonDisabled() {
  //     if (this.typeActive.includes('DHCP Client')) {
  //       return !this.form.interface_ip || !this.form.subnet_mask || !this.form.gateway_ip
  //     }
  //     if (this.typeActive.includes('PPPoE')) {
  //       return !this.form.interface_ip || !this.form.network_ip
  //     }
  //     return false
  //   },
  //   saveButtonError() {
  //     if (this.typeActive.includes('DHCP Client')) {
  //       const missingFields = []
  //       if (!this.form.interface_ip) missingFields.push('Interface')
  //       if (!this.form.subnet_mask) missingFields.push('Subnet Mask')
  //       if (!this.form.gateway_ip) missingFields.push('Gateways Address')
  //       return missingFields.join(', ')
  //     }
  //     if (this.typeActive.includes('PPPoE')) {
  //       const missingFields = []
  //       if (!this.form.interface_ip) missingFields.push('Interface')
  //       if (!this.form.network_ip) missingFields.push('Network Address')
  //       return missingFields.join(', ')
  //     }
  //     return ''
  //   },
  // },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    pppoePort() {
      return (this.pppoeStatus && this.pppoeStatus.port) || this.form.port || ''
    },
    pppoeStatusText() {
      const status = this.pppoeStatus

      if (!status) {
        return this.isPppoeStatusPolling ? 'Checking the controller...' : '-'
      }
      if (status.apply_error) {
        return `Not applied: ${status.apply_error}`
      }
      if (!status.is_applied) {
        return 'Applying the settings...'
      }
      if (status.is_connected) {
        return `Connected, ${status.interface_ip || 'no address yet'}`
      }
      if (this.isPppoeStatusPolling) {
        return 'Connecting...'
      }

      return `Not connected: ${status.message || 'the controller log gives no reason.'}`
    },
    pppoeStatusClass() {
      const status = this.pppoeStatus

      if (!status || this.isPppoeStatusPolling) {
        return 'text-muted'
      }

      return status.is_applied && status.is_connected && !status.apply_error ? 'text-success' : 'text-danger'
    },
  },
  async created() {
    this.getWANInfo()
  },
  beforeDestroy() {
    this.stopPppoeStatusPolling()
  },
  methods: {
    closeModal() {
      this.itemToEdit = null
    },
    checkFields() {
      this.$refs.licenceCreate.reset()
      if (this.typeActive.includes('PPPoE')) {
        this.form = {
          ...this.formRaw.pppoe,
          ...this.formRaw,
        }
      }
      if (this.typeActive.includes('DHCP Client')) {
        this.form = this.formRaw.dhcp
      }
      if (this.typeActive.includes('Static')) {
        this.form = this.formRaw.static
      }
    },
    getTypeEnum(label) {
      const labelRemoved = label.replace(' ☑️', '')
      const types = {
        'DHCP Client': 'dhcp',
        Static: 'static',
        PPPoE: 'pppoe',
      }

      return types[labelRemoved]
    },
    formatTypeBack(type) {
      if (!type) return ''
      const types = {
        dhcp: 'DHCP Client ☑️',
        static: 'Static ☑️',
        pppoe: 'PPPoE ☑️',
      }

      this.types = this.typesStatic.map(item => {
        if (types[type] === `${item} ☑️`) {
          return `${item} ☑️`
        }
        return item
      })

      return types[type]
    },
    async getWANInfo() {
      axios.get(`/site/${this.siteId}/network/setting/wan`)
        .then(response => {
          this.isLoading = false
          this.applyWANInfo(response.data.data)
        })
        .catch(({ response }) => {
          this.isLoading = false
          console.warn(response)
        })
    },
    async fetchPppoeStatus() {
      const { data } = await axios.get(`/site/${this.siteId}/network/setting/wan/pppoe-status`)
      this.pppoeStatus = data.data

      return this.pppoeStatus
    },
    startPppoeStatusPolling() {
      this.stopPppoeStatusPolling()
      this.pppoeStatus = null
      this.isPppoeStatusPolling = true

      let attempts = 0
      const poll = async () => {
        attempts += 1

        try {
          const status = await this.fetchPppoeStatus()
          const isDone = status.apply_error || (status.is_applied && status.is_connected)

          if (!isDone && attempts < PPPOE_STATUS_ATTEMPTS) {
            this.pppoeStatusTimer = setTimeout(poll, PPPOE_STATUS_INTERVAL)

            return
          }
        } catch (error) {
          console.warn(error)
        }

        this.isPppoeStatusPolling = false
      }

      poll()
    },
    stopPppoeStatusPolling() {
      clearTimeout(this.pppoeStatusTimer)
      this.pppoeStatusTimer = null
      this.isPppoeStatusPolling = false
    },
    async pullFromSite() {
      try {
        this.$refs.modalLoader.show()
        const response = await axios.put(`/site/${this.siteId}/network/setting/wan/pull`)
        this.applyWANInfo(response.data.data)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Data pulled from the controller',
            icon: 'BellIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        console.warn(error)
      } finally {
        this.$refs.modalLoader.hide()
      }
    },
    applyWANInfo(data) {
      const { type } = data

      this.isFromController = !!data.is_from_controller

      if (!type) {
        this.formRaw = {
          dhcp: {
            gateway_ip: '',
            interface_ip: '',
            subnet_mask: '',
          },
          pppoe: {
            interface_ip: '',
            network_ip: '',
            username: '',
            password: '',
          },
          static: {
            interface_ip: '',
            subnet_mask: '',
            gateway_ip: '',
          },
        }
      } else {
        this.formRaw = {
          ...data.payload,
          password: data.payload.password ? data.payload.password : data.default.pppoe.password,
          username: data.payload.username ? data.payload.username : data.default.pppoe.username,
          dhcp: {
            gateway_ip: type === 'dhcp' ? data.payload.gateway_ip : data.default.dhcp.gateway_ip,
            interface_ip: type === 'dhcp' ? data.payload.interface_ip : data.default.dhcp.interface_ip,
            subnet_mask: type === 'dhcp' ? data.payload.subnet_mask : data.default.dhcp.subnet_mask,
          },
          pppoe: {
            interface_ip: type === 'pppoe' ? data.payload.interface_ip : data.default.pppoe.interface_ip,
            network_ip: type === 'pppoe' ? data.payload.network_ip : data.default.pppoe.network_ip,
            username: type === 'pppoe' ? data.payload.username : data.default.pppoe.username,
            password: type === 'pppoe' ? data.payload.password : data.default.pppoe.password,
          },
          static: type === 'static' ? data.payload : {
            interface_ip: '',
            subnet_mask: '',
            gateway_ip: '',
          },
        }
      }
      this.typeActive = this.formatTypeBack(type, data.is_active)
      this.checkFields()

      // The connection status explains a missing address, so it is shown instead of a field error.
      if (type === 'pppoe' && !this.isPppoeStatusPolling) {
        this.fetchPppoeStatus().catch(error => console.warn(error))
      }
    },
    async validationForm() {
      const isValid = await this.$refs.licenceCreate.validate()
      if (isValid) {
        try {
          const payload = {
            ...this.form,
            type: this.getTypeEnum(this.typeActive),
          }
          const response = await axios.put(`site/${this.siteId}/network/setting/wan`, payload)

          if (payload.type === 'pppoe') {
            this.startPppoeStatusPolling()
          }

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Updated!',
              icon: 'BellIcon',
              variant: 'success',
            },
          })
          this.form = {
            ...this.form,
            dhcp: {
              ...response.data.data.type === 'dhcp' ? response.data.data.payload.interface_ip : '',
            },
          }
          this.getWANInfo()
        } catch (error) {
          const { status } = error.response

          if (status === 422) {
            const { fields } = error.response.data
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Validation error',
                icon: 'BellIcon',
                variant: 'danger',
              },
            })
            this.$refs.licenceCreate.setErrors(fields)
          }
        }
      } else {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            icon: 'BellIcon',
            variant: 'danger',
          },
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.wan-settings-header {
  margin-bottom: 24px;
}

.wan-pppoe-connection {
  min-height: 38px;
}
</style>
